/**
 * Campanha multi-agência: usuários de contas distintas só veem veículos (sites) permitidos.
 * Contas 87 e 544: veem todos os veículos. account_id null (ex.: time interno): sem restrição extra.
 *
 * Formato dos filtros alinhado com `ads-api/resolvers/build_query.js`:
 * - Sem `$and` / `$in` estilo Mongo no JSON: o parser faz JSON.parse e só entende
 *   número (=), array (whereIn), string (LIKE), null.
 * - Na query raiz `campaign(filter)`, o JOIN de veículo é placements → formats;
 *   usar `formats.site_id` (não `sites.site_id`, pois a tabela `sites` não entra nessa query).
 */
export const CROSS_AGENCY_CAMPAIGN_ID = 7143;

/** Coluna SQL na query `Query.campaign` (join placements + formats). */
const CAMPAIGN_FILTER_SITE_COLUMN = "formats.site_id";

const ACCOUNT_SITE_ALLOWLIST = {
  14: [99, 3512, 3466, 3359, 2092, 17, 1200, 3522, 1694],
  245: [1, 2105, 3710],
  87: null,
  544: null,
  49: [35, 3695, 2160, 3452, 3534],
};

export function isCrossAgencyCampaign7143(campaignId) {
  return Number(campaignId) === CROSS_AGENCY_CAMPAIGN_ID;
}

/**
 * @returns {null|number[]} null = todos os sites; [] = nenhum
 */
export function getAllowedSiteIds7143(accountId) {
  if (accountId == null || accountId === "") {
    return null;
  }
  const n = Number(accountId);
  if (!Number.isFinite(n)) {
    return [];
  }
  if (!Object.prototype.hasOwnProperty.call(ACCOUNT_SITE_ALLOWLIST, n)) {
    return [];
  }
  const v = ACCOUNT_SITE_ALLOWLIST[n];
  if (v === null) {
    return null;
  }
  return [...v];
}

export function siteAllowedForCampaign7143(campaignId, accountId, siteId) {
  if (!isCrossAgencyCampaign7143(campaignId)) {
    return true;
  }
  const sid = Number(siteId);
  if (!Number.isFinite(sid)) {
    return false;
  }
  const allowed = getAllowedSiteIds7143(accountId);
  if (allowed === null) {
    return true;
  }
  return allowed.includes(sid);
}

/** Lista literal SQL no filtro: [1,2,3] → whereIn (ver build_query.js). */
function sqlIdArrayLiteral(ids) {
  return `[${ids.join(",")}]`;
}

/**
 * Filtro único objeto no `campaign(filter)` para 7143 + restrição de veículo (query SQL).
 */
function build7143CampaignFilterWithSiteSql(campaignId, siteColumnLiteral) {
  const cid = Number(campaignId);
  return `{'campaigns.campaign_id': ${cid}, ${siteColumnLiteral}}`;
}

/**
 * Filtro no resolver `campaign(filter: ...)` — linha SQL em `campaigns` + joins; usa `formats.site_id`.
 */
export function buildCampaignAggregationFilter(
  campaignId,
  accountId,
  routeSiteId
) {
  const cid = Number(campaignId);
  const simple = `{'campaigns.campaign_id': ${cid}}`;

  if (!isCrossAgencyCampaign7143(cid)) {
    return simple;
  }

  const allowed = getAllowedSiteIds7143(accountId);
  const routeSite =
    routeSiteId != null && routeSiteId !== "" ? Number(routeSiteId) : null;
  const hasRouteSite = Number.isFinite(routeSite);

  const col = `'${CAMPAIGN_FILTER_SITE_COLUMN}'`;

  if (allowed === null) {
    if (hasRouteSite) {
      return build7143CampaignFilterWithSiteSql(
        cid,
        `${col}: ${routeSite}`
      );
    }
    return simple;
  }

  if (hasRouteSite) {
    if (!allowed.includes(routeSite)) {
      return build7143CampaignFilterWithSiteSql(cid, `${col}: []`);
    }
    return build7143CampaignFilterWithSiteSql(cid, `${col}: ${routeSite}`);
  }

  if (!allowed.length) {
    return build7143CampaignFilterWithSiteSql(cid, `${col}: []`);
  }
  return build7143CampaignFilterWithSiteSql(
    cid,
    `${col}: ${sqlIdArrayLiteral(allowed)}`
  );
}

/**
 * Filtro para `sites(filter: ...)` — query parte de `db.referential('sites')` → usar `sites.site_id`.
 */
export function buildSitesNestedFilter(campaignId, accountId, routeSiteId) {
  const cid = Number(campaignId);
  const routeSite =
    routeSiteId != null && routeSiteId !== "" ? Number(routeSiteId) : null;
  const hasRouteSite = Number.isFinite(routeSite);

  if (!isCrossAgencyCampaign7143(cid)) {
    if (hasRouteSite) {
      return `{'sites.site_id': ${routeSite}}`;
    }
    return null;
  }

  const allowed = getAllowedSiteIds7143(accountId);

  if (allowed === null) {
    if (hasRouteSite) {
      return `{'sites.site_id': ${routeSite}}`;
    }
    return null;
  }

  if (hasRouteSite) {
    if (!allowed.includes(routeSite)) {
      return `{'sites.site_id': []}`;
    }
    return `{'sites.site_id': ${routeSite}}`;
  }

  if (!allowed.length) {
    return `{'sites.site_id': []}`;
  }
  return `{'sites.site_id': ${sqlIdArrayLiteral(allowed)}}`;
}

/**
 * Filtro para `channels(filter: ...)` — query em `channels` com `channels.site_id`.
 */
export function buildChannelsNestedFilter(
  campaignId,
  accountId,
  routeSiteId
) {
  const key = "channels.site_id";
  const cid = Number(campaignId);
  const routeSite =
    routeSiteId != null && routeSiteId !== "" ? Number(routeSiteId) : null;
  const hasRouteSite = Number.isFinite(routeSite);

  if (!isCrossAgencyCampaign7143(cid)) {
    if (hasRouteSite) {
      return `{'${key}': ${routeSite}}`;
    }
    return null;
  }

  const allowed = getAllowedSiteIds7143(accountId);

  if (allowed === null) {
    if (hasRouteSite) {
      return `{'${key}': ${routeSite}}`;
    }
    return null;
  }

  if (hasRouteSite) {
    if (!allowed.includes(routeSite)) {
      return `{'${key}': []}`;
    }
    return `{'${key}': ${routeSite}}`;
  }

  if (!allowed.length) {
    return `{'${key}': []}`;
  }
  return `{'${key}': ${sqlIdArrayLiteral(allowed)}}`;
}
